import React, { useState, useEffect } from 'react';
import './App.css';

// AWS Cognito configuration
const cognitoConfig = {
  userPoolId: 'us-east-1_UMXkHoxgH',
  clientId: '1u45n5um5qqdvi9tr4cvuiir86',
  region: 'us-east-1'
};

// In-memory token storage
let authTokens = {
  idToken: null,
  accessToken: null,
  refreshToken: null,
  expiration: null,
  username: null
};

// Auth functions
const auth = {
  signIn: async (username, password) => {
    try {
      const response = await fetch(`https://cognito-idp.${cognitoConfig.region}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
        },
        body: JSON.stringify({
          AuthFlow: 'USER_PASSWORD_AUTH',
          ClientId: cognitoConfig.clientId,
          AuthParameters: {
            USERNAME: username,
            PASSWORD: password
          }
        })
      });

      const data = await response.json();

      if (data.AuthenticationResult) {
        authTokens = {
          idToken: data.AuthenticationResult.IdToken,
          accessToken: data.AuthenticationResult.AccessToken,
          refreshToken: data.AuthenticationResult.RefreshToken,
          expiration: new Date().getTime() + data.AuthenticationResult.ExpiresIn * 1000,
          username
        };
        localStorage.setItem('meyerbank_auth', JSON.stringify(authTokens));
        return { success: true, username };
      } else if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        return {
          success: false,
          challengeName: 'NEW_PASSWORD_REQUIRED',
          session: data.Session
        };
      } else {
        throw new Error('Authentication failed');
      }
    } catch (error) {
      console.error('Sign in error:', error);
      return { success: false, error: error.message || 'Authentication failed' };
    }
  },
  
  completeNewPasswordChallenge: async (username, newPassword, session) => {
    try {
      const response = await fetch(`https://cognito-idp.${cognitoConfig.region}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge'
        },
        body: JSON.stringify({
          ChallengeName: 'NEW_PASSWORD_REQUIRED',
          ClientId: cognitoConfig.clientId,
          ChallengeResponses: {
            USERNAME: username,
            NEW_PASSWORD: newPassword
          },
          Session: session
        })
      });

      const data = await response.json();

      if (data.AuthenticationResult) {
        authTokens = {
          idToken: data.AuthenticationResult.IdToken,
          accessToken: data.AuthenticationResult.AccessToken,
          refreshToken: data.AuthenticationResult.RefreshToken,
          expiration: new Date().getTime() + data.AuthenticationResult.ExpiresIn * 1000,
          username
        };
        localStorage.setItem('meyerbank_auth', JSON.stringify(authTokens));
        return { success: true, username };
      } else {
        throw new Error('Failed to complete password challenge');
      }
    } catch (error) {
      console.error('Complete new password challenge error:', error);
      return { success: false, error: error.message || 'Failed to complete password challenge' };
    }
  },
  
  signOut: () => {
    authTokens = {
      idToken: null,
      accessToken: null,
      refreshToken: null,
      expiration: null,
      username: null
    };
    localStorage.removeItem('meyerbank_auth');
    return { success: true };
  },
  
  isAuthenticated: () => {
    if (!authTokens.accessToken) return false;
    if (authTokens.expiration && new Date().getTime() > authTokens.expiration) {
      return false;
    }
    return true;
  },
  
  getCurrentUser: () => {
    if (!auth.isAuthenticated()) return null;
    return { username: authTokens.username };
  },
  
  getUserAttributes: async () => {
    if (!auth.isAuthenticated()) return null;
    
    try {
      const response = await fetch(`https://cognito-idp.${cognitoConfig.region}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.GetUser'
        },
        body: JSON.stringify({
          AccessToken: authTokens.accessToken
        })
      });
      
      return await response.json();
    } catch (error) {
      console.error('Error getting user attributes:', error);
      return null;
    }
  },
  
  getIdToken: () => {
    if (!auth.isAuthenticated()) return null;
    return authTokens.idToken;
  },
  
  getAccessToken: () => {
    if (!auth.isAuthenticated()) return null;
    return authTokens.accessToken;
  }
};

// API functions
const api = {
  getCheckingStatement: async () => {
    try {
      // Get the ID token instead of access token
      const idToken = authTokens.idToken;
      
      if (!idToken) {
        throw new Error('Not authenticated');
      }
      
      // Call the API Gateway endpoint with ID token
      const response = await fetch('https://s0r7vik0ki.execute-api.us-east-1.amazonaws.com/prod/accounts/checking', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Get checking statement error:', error);
      // Fallback to mock data if API fails
      return {
        success: true,
        data: {
          accountNumber: '123456789',
          balance: 5000.00,
          currency: 'USD',
          transactions: [
            { id: 1, date: '2025-05-20', description: 'Salary', amount: 3000.00, type: 'credit' },
            { id: 2, date: '2025-05-21', description: 'Grocery Store', amount: -150.00, type: 'debit' },
            { id: 3, date: '2025-05-22', description: 'Gas Station', amount: -45.00, type: 'debit' },
            { id: 4, date: '2025-05-23', description: 'Restaurant', amount: -85.00, type: 'debit' },
            { id: 5, date: '2025-05-24', description: 'Online Shopping', amount: -120.00, type: 'debit' }
          ]
        }
      };
    }
  },
  
  getInvestmentStatement: async () => {
    try {
      // Get the ID token
      const idToken = authTokens.idToken;
      
      if (!idToken) {
        throw new Error('Not authenticated');
      }
      
      // Call the API Gateway endpoint with ID token
      const response = await fetch('https://s0r7vik0ki.execute-api.us-east-1.amazonaws.com/prod/accounts/investment', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Get investment statement error:', error);
      // Fallback to mock data if API fails
      return {
        success: true,
        data: {
          accountNumber: '987654321',
          balance: 15000.00,
          currency: 'USD',
          transactions: [
            { id: 1, date: '2025-05-15', description: 'Initial Investment', amount: 10000.00, type: 'credit' },
            { id: 2, date: '2025-05-18', description: 'Stock Purchase - AAPL', amount: -2000.00, type: 'debit' },
            { id: 3, date: '2025-05-19', description: 'Stock Purchase - MSFT', amount: -1500.00, type: 'debit' },
            { id: 4, date: '2025-05-20', description: 'Dividend Payment', amount: 50.00, type: 'credit' },
            { id: 5, date: '2025-05-22', description: 'Stock Sale - TSLA', amount: 2200.00, type: 'credit' }
          ]
        }
      };
    }
  },
  
  transferFunds: async (fromAccount, toAccount, amount) => {
    try {
      // Get the ID token
      const idToken = authTokens.idToken;
      
      if (!idToken) {
        throw new Error('Not authenticated');
      }
      
      // Call the API Gateway endpoint with ID token
      const response = await fetch('https://s0r7vik0ki.execute-api.us-east-1.amazonaws.com/prod/accounts/transfers', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fromAccount,
          toAccount,
          amount
        })
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Transfer funds error:', error);
      // Fallback to mock response if API fails
      const isScheduled = fromAccount === 'investment' && toAccount === 'checking';
      const completionDate = isScheduled ? 
        new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : 
        new Date().toISOString().split('T')[0];
      
      return {
        success: true,
        data: {
          transferId: Math.floor(Math.random() * 1000000),
          fromAccount,
          toAccount,
          amount,
          status: isScheduled ? 'scheduled' : 'completed',
          scheduledDate: new Date().toISOString().split('T')[0],
          completionDate,
          message: isScheduled ? 
            'Transfer scheduled. Funds will be available in 5 days.' : 
            'Transfer completed successfully.'
        }
      };
    }
  },
  
  getExchangeRate: async () => {
    try {
      // Get the ID token
      const idToken = authTokens.idToken;
      
      if (!idToken) {
        throw new Error('Not authenticated');
      }
      
      // Call the API Gateway endpoint with ID token
      const response = await fetch('https://s0r7vik0ki.execute-api.us-east-1.amazonaws.com/prod/exchange/rate', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Get exchange rate error:', error);
      // Fallback to mock data if API fails
      return {
        success: true,
        data: {
          base: 'USD',
          target: 'BRL',
          rate: 5.25,
          lastUpdated: new Date().toISOString()
        }
      };
    }
  },
  
  getScheduledTransfers: async () => {
    try {
      // Get the ID token
      const idToken = authTokens.idToken;
      
      if (!idToken) {
        throw new Error('Not authenticated');
      }
      
      // Call the API Gateway endpoint with ID token
      const response = await fetch('https://s0r7vik0ki.execute-api.us-east-1.amazonaws.com/prod/accounts/scheduled', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Get scheduled transfers error:', error);
      // Fallback to mock data if API fails
      return {
        success: true,
        data: {
          transfers: [
            {
              transferId: 123456,
              fromAccount: 'investment',
              toAccount: 'checking',
              amount: 1000.00,
              status: 'scheduled',
              scheduledDate: '2025-05-25',
              completionDate: '2025-05-30'
            },
            {
              transferId: 123457,
              fromAccount: 'investment',
              toAccount: 'checking',
              amount: 500.00,
              status: 'processing',
              scheduledDate: '2025-05-22',
              completionDate: '2025-05-27'
            }
          ]
        }
      };
    }
  }
};

// Load tokens from localStorage on initialization
try {
  const savedTokens = localStorage.getItem('meyerbank_auth');
  if (savedTokens) {
    authTokens = JSON.parse(savedTokens);
  }
} catch (error) {
  console.error('Error loading auth tokens:', error);
}

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [session, setSession] = useState(null);
  const [userName, setUserName] = useState('');

  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Application state
  const [activeTab, setActiveTab] = useState('home');
  const [checkingData, setCheckingData] = useState(null);
  const [investmentData, setInvestmentData] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [scheduledTransfers, setScheduledTransfers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Transfer form state
  const [transferAmount, setTransferAmount] = useState('');
  const [transferFrom, setTransferFrom] = useState('checking');
  const [transferTo, setTransferTo] = useState('investment');
  const [transferMessage, setTransferMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  // Exchange form state
  const [exchangeAmount, setExchangeAmount] = useState('');
  const [exchangeResult, setExchangeResult] = useState('');

  // Check authentication status on load
  useEffect(() => {
    const checkAuth = async () => {
      if (auth.isAuthenticated()) {
        const currentUser = auth.getCurrentUser();
        setIsAuthenticated(true);
        setUser(currentUser);
        
        // Get user attributes to display name
        try {
          const response = await fetch(`https://cognito-idp.${cognitoConfig.region}.amazonaws.com/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-amz-json-1.1',
              'X-Amz-Target': 'AWSCognitoIdentityProviderService.GetUser'
            },
            body: JSON.stringify({
              AccessToken: authTokens.accessToken
            })
          });
          
          const userData = await response.json();
          if (userData.UserAttributes) {
            const nameAttribute = userData.UserAttributes.find(attr => attr.Name === 'name');
            if (nameAttribute) {
              setUserName(nameAttribute.Value);
            } else {
              // Fallback to username if name attribute not found
              setUserName(currentUser.username);
            }
          }
        } catch (error) {
          console.error('Error getting user attributes:', error);
          setUserName(currentUser.username);
        }
      }
      setAuthLoading(false);
    };

    checkAuth();
  }, []);

  // Load account data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadAccountData();
    }
  }, [isAuthenticated]);

  // Load account data
  const loadAccountData = async () => {
    setLoading(true);
    
    // Get checking account data
    const checkingResult = await api.getCheckingStatement();
    if (checkingResult.success) {
      setCheckingData(checkingResult.data);
    }
    
    // Get investment account data
    const investmentResult = await api.getInvestmentStatement();
    if (investmentResult.success) {
      setInvestmentData(investmentResult.data);
    }
    
    // Get exchange rate
    const rateResult = await api.getExchangeRate();
    if (rateResult.success) {
      setExchangeRate(rateResult.data);
    }
    
    // Get scheduled transfers
    const transfersResult = await api.getScheduledTransfers();
    if (transfersResult.success) {
      setScheduledTransfers(transfersResult.data.transfers);
    }
    
    setLoading(false);
  };

  // Handle login form submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setAuthLoading(true);
    
    const result = await auth.signIn(username, password);
    
    if (result.success) {
      setIsAuthenticated(true);
      setUser({ username: result.username });
      
      // Get user attributes after successful login
      try {
        const response = await fetch(`https://cognito-idp.${cognitoConfig.region}.amazonaws.com/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.GetUser'
          },
          body: JSON.stringify({
            AccessToken: authTokens.accessToken
          })
        });
        
        const userData = await response.json();
        if (userData.UserAttributes) {
          const nameAttribute = userData.UserAttributes.find(attr => attr.Name === 'name');
          if (nameAttribute) {
            setUserName(nameAttribute.Value);
          } else {
            setUserName(result.username);
          }
        }
      } catch (error) {
        console.error('Error getting user attributes:', error);
        setUserName(result.username);
      }
    } else if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
      setNewPasswordRequired(true);
      setSession(result.session);
    } else {
      setLoginError(result.error || 'Login failed');
    }
    
    setAuthLoading(false);
  };

  // Handle new password submission
  const handleNewPassword = async (e) => {
    e.preventDefault();
    setLoginError('');
    setAuthLoading(true);
    
    const result = await auth.completeNewPasswordChallenge(username, newPassword, session);
    
    if (result.success) {
      setIsAuthenticated(true);
      setUser({ username: result.username });
      setNewPasswordRequired(false);
      setSession(null);
      
      // Get user attributes after password change
      try {
        const response = await fetch(`https://cognito-idp.${cognitoConfig.region}.amazonaws.com/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.GetUser'
          },
          body: JSON.stringify({
            AccessToken: authTokens.accessToken
          })
        });
        
        const userData = await response.json();
        if (userData.UserAttributes) {
          const nameAttribute = userData.UserAttributes.find(attr => attr.Name === 'name');
          if (nameAttribute) {
            setUserName(nameAttribute.Value);
          } else {
            setUserName(result.username);
          }
        }
      } catch (error) {
        console.error('Error getting user attributes:', error);
        setUserName(result.username);
      }
    } else {
      setLoginError(result.error || 'Failed to set new password');
    }
    
    setAuthLoading(false);
  };

  // Handle logout
  const handleLogout = () => {
    auth.signOut();
    setIsAuthenticated(false);
    setUser(null);
    setActiveTab('checking');
  };

  // Handle transfer form submission
  const handleTransfer = async (e) => {
    e.preventDefault();
    setTransferMessage('');
    setLoading(true);
    
    const amount = parseFloat(transferAmount);
    if (isNaN(amount) || amount <= 0) {
      setTransferMessage('Please enter a valid amount');
      setLoading(false);
      return;
    }
    
    const result = await api.transferFunds(transferFrom, transferTo, amount);
    
    if (result.success) {
      // Display the message from the API response
      setTransferMessage(result.data.message || 'Transfer processed successfully.');
      setTransferAmount('');
      
      // Set message type based on status
      setMessageType(result.data.status === 'scheduled' ? 'info' : 'success');
      
      // Reload account data
      await loadAccountData();
    } else {
      // Display error message from API
      setTransferMessage(result.error || 'Transfer failed');
      setMessageType('error');
    }
    
    setLoading(false);
  };

  // Handle exchange calculation
  const handleExchangeCalculate = (e) => {
    e.preventDefault();
    
    const amount = parseFloat(exchangeAmount);
    if (isNaN(amount) || amount <= 0) {
      setExchangeResult('Please enter a valid amount');
      return;
    }
    
    if (exchangeRate) {
      const result = amount * exchangeRate.rate;
      setExchangeResult(`${amount.toFixed(2)} USD = ${result.toFixed(2)} BRL`);
    } else {
      setExchangeResult('Exchange rate not available');
    }
  };

  // Render login form
  if (!isAuthenticated) {
    return (
      <div className="app">
        <header className="header">
          <h1>Meyer Bank</h1>
        </header>
        
        <main className="main">
          {authLoading ? (
            <div className="loading">Loading...</div>
          ) : newPasswordRequired ? (
            <div className="login-container">
              <h2>Set New Password</h2>
              <p>You need to set a new password for your first login.</p>
              
              <form onSubmit={handleNewPassword}>
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    value={username}
                    disabled
                  />
                </div>
                
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>
                
                {loginError && <div className="error">{loginError}</div>}
                
                <button type="submit" className="btn">Set New Password</button>
              </form>
            </div>
          ) : (
            <div className="login-container">
              <h2>Login to Your Account</h2>
              
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                
                {loginError && <div className="error">{loginError}</div>}
                
                <button type="submit" className="btn">Login</button>
              </form>
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="user-info">
          Welcome, {userName || user.username}
        </div>
        <h1 className="bank-title" onClick={() => setActiveTab('home')}>Meyer Bank</h1>
        <button onClick={handleLogout} className="logout-btn">Logout</button>
      </header>
      
      <nav className="nav">
        <ul>
          <li>
            <button
              className={activeTab === 'checking' ? 'active' : ''}
              onClick={() => setActiveTab('checking')}
            >
              Checking Account
            </button>
          </li>
          <li>
            <button
              className={activeTab === 'investment' ? 'active' : ''}
              onClick={() => setActiveTab('investment')}
            >
              Investment Account
            </button>
          </li>
          <li>
            <button
              className={activeTab === 'transfer' ? 'active' : ''}
              onClick={() => setActiveTab('transfer')}
            >
              Transfer Funds
            </button>
          </li>
          <li>
            <button
              className={activeTab === 'exchange' ? 'active' : ''}
              onClick={() => setActiveTab('exchange')}
            >
              Exchange Rate
            </button>
          </li>
          <li>
            <button
              className={activeTab === 'scheduled' ? 'active' : ''}
              onClick={() => setActiveTab('scheduled')}
            >
              Scheduled Transfers
            </button>
          </li>
        </ul>
      </nav>
      
      <main className="main">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <>
            {activeTab === 'home' && checkingData && investmentData && exchangeRate && (
              <div className="home-section">
                <h2>Welcome to Meyer Bank</h2>
                
                <div className="dashboard-grid">
                  {/* Account Summary Card */}
                  <div className="dashboard-card account-summary">
                    <h3>Account Summary</h3>
                    <div className="balance-container">
                      <div className="balance-item">
                        <div className="balance-label">Checking</div>
                        <div className="balance-value">${checkingData.balance.toFixed(2)}</div>
                      </div>
                      <div className="balance-item">
                        <div className="balance-label">Investment</div>
                        <div className="balance-value">${investmentData.balance.toFixed(2)}</div>
                      </div>
                      <div className="balance-item total">
                        <div className="balance-label">Total Assets</div>
                        <div className="balance-value">${(checkingData.balance + investmentData.balance).toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Exchange Rate Card */}
                  <div className="dashboard-card exchange-summary">
                    <h3>Currency Exchange</h3>
                    <div className="exchange-rate-display">
                      <div className="rate-value">1 USD = {exchangeRate.rate.toFixed(2)} BRL</div>
                      <div className="rate-date">Updated: {new Date(exchangeRate.lastUpdated).toLocaleDateString()}</div>
                    </div>
                    <div className="total-in-brl">
                      <div className="brl-label">Your Total in BRL</div>
                      <div className="brl-value">R${((checkingData.balance + investmentData.balance) * exchangeRate.rate).toFixed(2)}</div>
                    </div>
                  </div>
                  
                  {/* Quick Actions Card */}
                  <div className="dashboard-card quick-actions">
                    <h3>Quick Actions</h3>
                    <div className="action-buttons">
                      <button onClick={() => setActiveTab('transfer')} className="action-btn transfer-btn">
                        Transfer Funds
                      </button>
                      <button onClick={() => setActiveTab('checking')} className="action-btn view-btn">
                        View Transactions
                      </button>
                      <button onClick={() => setActiveTab('scheduled')} className="action-btn scheduled-btn">
                        Scheduled Transfers
                      </button>
                    </div>
                  </div>
                  
                  {/* Recent Activity Card */}
                  <div className="dashboard-card recent-activity">
                    <h3>Recent Activity</h3>
                    <div className="recent-transactions">
                      {[...checkingData.transactions, ...investmentData.transactions]
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .slice(0, 5)
                        .map((transaction, index) => (
                          <div key={index} className="transaction-item">
                            <div className="transaction-date">{transaction.date}</div>
                            <div className="transaction-desc">{transaction.description}</div>
                            <div className={`transaction-amount ${transaction.type === 'credit' ? 'credit' : 'debit'}`}>
                              {transaction.type === 'credit' ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
                            </div>
                          </div>
                        ))
                      }
                    </div>
                    <div className="view-all">
                      <button onClick={() => setActiveTab('checking')} className="view-all-btn">
                        View All Transactions
                      </button>
                    </div>
                  </div>
                  
                  {/* Financial Overview Card */}
                  <div className="dashboard-card financial-overview">
                    <h3>Financial Overview</h3>
                    <div className="chart-container">
                      <div className="donut-chart">
                        <div className="chart-placeholder">
                          <div className="chart-circle" style={{
                            background: `conic-gradient(
                              var(--texas-blue) 0% ${(checkingData.balance / (checkingData.balance + investmentData.balance)) * 100}%, 
                              var(--texas-red) ${(checkingData.balance / (checkingData.balance + investmentData.balance)) * 100}% 100%
                            )`
                          }}></div>
                          <div className="chart-center"></div>
                        </div>
                        <div className="chart-legend">
                          <div className="legend-item">
                            <div className="legend-color" style={{backgroundColor: 'var(--texas-blue)'}}></div>
                            <div className="legend-label">Checking ({Math.round((checkingData.balance / (checkingData.balance + investmentData.balance)) * 100)}%)</div>
                          </div>
                          <div className="legend-item">
                            <div className="legend-color" style={{backgroundColor: 'var(--texas-red)'}}></div>
                            <div className="legend-label">Investment ({Math.round((investmentData.balance / (checkingData.balance + investmentData.balance)) * 100)}%)</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Scheduled Transfers Preview */}
                  <div className="dashboard-card scheduled-preview">
                    <h3>Upcoming Transfers</h3>
                    {scheduledTransfers && scheduledTransfers.length > 0 ? (
                      <div className="scheduled-list">
                        {scheduledTransfers
                          .filter(transfer => transfer.status === 'scheduled')
                          .slice(0, 2)
                          .map((transfer, index) => (
                            <div key={index} className="scheduled-item">
                              <div className="transfer-info">
                                <div className="transfer-accounts">
                                  {transfer.fromAccount.charAt(0).toUpperCase() + transfer.fromAccount.slice(1)} → {transfer.toAccount.charAt(0).toUpperCase() + transfer.toAccount.slice(1)}
                                </div>
                                <div className="transfer-amount">${transfer.amount.toFixed(2)}</div>
                              </div>
                              <div className="transfer-dates">
                                <div className="completion-date">Completes on: {transfer.completionDate}</div>
                              </div>
                            </div>
                          ))
                        }
                        <button onClick={() => setActiveTab('scheduled')} className="view-all-btn">
                          View All Scheduled Transfers
                        </button>
                      </div>
                    ) : (
                      <div className="no-transfers">No upcoming transfers</div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'checking' && checkingData && (
              <div className="account-section">
                <h2>Checking Account</h2>
                <div className="account-info">
                  <p><strong>Account Number:</strong> {checkingData.accountNumber}</p>
                  <p><strong>Balance:</strong> ${checkingData.balance.toFixed(2)}</p>
                </div>
                
                <h3>Recent Transactions</h3>
                <table className="transactions-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkingData.transactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td>{transaction.date}</td>
                        <td>{transaction.description}</td>
                        <td className={transaction.type === 'credit' ? 'credit' : 'debit'}>
                          {transaction.type === 'credit' ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {activeTab === 'investment' && investmentData && (
              <div className="account-section">
                <h2>Investment Account</h2>
                <div className="account-info">
                  <p><strong>Account Number:</strong> {investmentData.accountNumber}</p>
                  <p><strong>Balance:</strong> ${investmentData.balance.toFixed(2)}</p>
                </div>
                
                {investmentData.holdings && (
                  <>
                    <h3>Holdings</h3>
                    <table className="holdings-table">
                      <thead>
                        <tr>
                          <th>Symbol</th>
                          <th>Name</th>
                          <th>Shares</th>
                          <th>Price</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {investmentData.holdings.map((holding, index) => (
                          <tr key={index}>
                            <td>{holding.symbol}</td>
                            <td>{holding.name}</td>
                            <td>{holding.shares}</td>
                            <td>${holding.price.toFixed(2)}</td>
                            <td>${holding.value.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
                
                <h3>Recent Transactions</h3>
                <table className="transactions-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {investmentData.transactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td>{transaction.date}</td>
                        <td>{transaction.description}</td>
                        <td className={transaction.type === 'credit' ? 'credit' : 'debit'}>
                          {transaction.type === 'credit' ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {activeTab === 'transfer' && (
              <div className="transfer-section">
                <h2>Transfer Funds</h2>
                
                <form onSubmit={handleTransfer}>
                  <div className="form-group">
                    <label>From Account</label>
                    <select
                      value={transferFrom}
                      onChange={(e) => {
                        setTransferFrom(e.target.value);
                        setTransferTo(e.target.value === 'checking' ? 'investment' : 'checking');
                      }}
                    >
                      <option value="checking">Checking Account</option>
                      <option value="investment">Investment Account</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>To Account</label>
                    <select
                      value={transferTo}
                      onChange={(e) => {
                        setTransferTo(e.target.value);
                        setTransferFrom(e.target.value === 'checking' ? 'investment' : 'checking');
                      }}
                    >
                      <option value="checking">Checking Account</option>
                      <option value="investment">Investment Account</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={transferAmount}
                      onChange={(e) => setTransferAmount(e.target.value)}
                      required
                    />
                  </div>
                  
                  <button type="submit" className="btn">Transfer</button>
                </form>
                
                {transferMessage && (
                  <div className={`message message-${messageType}`}>
                    {transferMessage}
                  </div>
                )}
                
                <div className="transfer-note">
                  <p><strong>Note:</strong> Transfers from Investment to Checking account take 5 days to process.</p>
                </div>
              </div>
            )}
            
            {activeTab === 'exchange' && exchangeRate && (
              <div className="exchange-section">
                <h2>Exchange Rate</h2>
                
                <div className="exchange-info">
                  <p><strong>Current Rate:</strong> 1 USD = {exchangeRate.rate.toFixed(2)} BRL</p>
                  <p><strong>Last Updated:</strong> {new Date(exchangeRate.lastUpdated).toLocaleString()}</p>
                </div>
                
                <h3>Account Balances in USD and BRL</h3>
                <table className="exchange-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>USD Balance</th>
                      <th>BRL Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkingData && (
                      <tr>
                        <td>Checking</td>
                        <td>${checkingData.balance.toFixed(2)}</td>
                        <td>R${(checkingData.balance * exchangeRate.rate).toFixed(2)}</td>
                      </tr>
                    )}
                    {investmentData && (
                      <tr>
                        <td>Investment</td>
                        <td>${investmentData.balance.toFixed(2)}</td>
                        <td>R${(investmentData.balance * exchangeRate.rate).toFixed(2)}</td>
                      </tr>
                    )}
                    {checkingData && investmentData && (
                      <tr className="total-row">
                        <td><strong>Total</strong></td>
                        <td><strong>${(checkingData.balance + investmentData.balance).toFixed(2)}</strong></td>
                        <td><strong>R${((checkingData.balance + investmentData.balance) * exchangeRate.rate).toFixed(2)}</strong></td>
                      </tr>
                    )}
                  </tbody>
                </table>
                
                <h3>Currency Converter</h3>
                <form onSubmit={handleExchangeCalculate}>
                  <div className="form-group">
                    <label>Amount in USD</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={exchangeAmount}
                      onChange={(e) => setExchangeAmount(e.target.value)}
                      required
                    />
                  </div>
                  
                  <button type="submit" className="btn">Calculate</button>
                </form>
                
                {exchangeResult && (
                  <div className="exchange-result">
                    {exchangeResult}
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'scheduled' && (
              <div className="scheduled-section">
                <h2>Scheduled Transfers</h2>
                
                {scheduledTransfers.length > 0 ? (
                  <table className="scheduled-table">
                    <thead>
                      <tr>
                        <th>Transfer ID</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Amount</th>
                        <th>Scheduled Date</th>
                        <th>Completion Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scheduledTransfers.map((transfer) => (
                        <tr key={transfer.transferId}>
                          <td>{transfer.transferId}</td>
                          <td>{transfer.fromAccount === 'checking' ? 'Checking' : 'Investment'}</td>
                          <td>{transfer.toAccount === 'checking' ? 'Checking' : 'Investment'}</td>
                          <td>${transfer.amount.toFixed(2)}</td>
                          <td>{transfer.scheduledDate}</td>
                          <td>{transfer.completionDate}</td>
                          <td>{transfer.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No scheduled transfers found.</p>
                )}
                
                <div className="scheduled-note">
                  <p><strong>Note:</strong> Transfers from Investment to Checking account take 5 days to process.</p>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default App;